import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Book,
  Plus,
  Edit2,
  Trash2,
  ArrowLeft,
  Loader2,
  DollarSign,
  TrendingDown,
  Wallet,
  CreditCard,
  Users
} from 'lucide-react';
import { accountingAPI } from '../services/api';

const ChartOfAccountsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('ALL'); // 'ALL', 'ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await accountingAPI.getAccounts();
      setAccounts(response.data || []);
    } catch (err) {
      console.error('Error fetching chart of accounts:', err);
      setError('Failed to load chart of accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedAccounts = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await accountingAPI.seedDefaultAccounts();
      const data = response.data || {};
      const created = data.accounts_created;
      if (typeof created === 'number' && created > 0) {
        alert(`Success! Created ${created} default accounts.`);
        await fetchAccounts();
      } else {
        alert(data.message || 'Default accounts created or already exist.');
        await fetchAccounts();
      }
    } catch (err) {
      console.error('Error seeding accounts:', err);
      setError(err.response?.data?.detail || 'Failed to create default accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (account) => {
    if (!window.confirm(`Delete account "${account.account_name}"? This is only allowed if it has no posted transactions.`)) return;
    setError(null);
    try {
      await accountingAPI.deleteAccount(account.id);
      await fetchAccounts();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to delete account. It may have posted transactions—try marking inactive instead.');
    }
  };

  // Group accounts by type for GAAP presentation
  const groupAccountsByType = () => {
    const grouped = {
      ASSET: [],
      LIABILITY: [],
      EQUITY: [],
      REVENUE: [],
      EXPENSE: [],
      COGS: []
    };

    accounts.forEach(account => {
      const type = account.account_type || '';
      if (grouped[type]) {
        grouped[type].push(account);
      }
    });

    // Sort each group by account number
    Object.keys(grouped).forEach(type => {
      grouped[type].sort((a, b) => {
        const numA = parseInt(a.account_number) || 0;
        const numB = parseInt(b.account_number) || 0;
        return numA - numB;
      });
    });

    return grouped;
  };

  const getAccountTypeIcon = (type) => {
    switch (type) {
      case 'ASSET':
        return <Wallet className="w-5 h-5 text-green-600" />;
      case 'LIABILITY':
        return <CreditCard className="w-5 h-5 text-red-600" />;
      case 'EQUITY':
        return <Users className="w-5 h-5 text-blue-600" />;
      case 'REVENUE':
        return <DollarSign className="w-5 h-5 text-emerald-600" />;
      case 'EXPENSE':
      case 'COGS':
        return <TrendingDown className="w-5 h-5 text-orange-600" />;
      default:
        return <Book className="w-5 h-5 text-gray-600" />;
    }
  };

  const getAccountTypeColor = (type) => {
    switch (type) {
      case 'ASSET':
        return 'bg-green-50 border-green-200 text-green-900';
      case 'LIABILITY':
        return 'bg-red-50 border-red-200 text-red-900';
      case 'EQUITY':
        return 'bg-blue-50 border-blue-200 text-blue-900';
      case 'REVENUE':
        return 'bg-emerald-50 border-emerald-200 text-emerald-900';
      case 'EXPENSE':
      case 'COGS':
        return 'bg-orange-50 border-orange-200 text-orange-900';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-900';
    }
  };

  const groupedAccounts = groupAccountsByType();
  const filteredGroups = filter === 'ALL'
    ? groupedAccounts
    : { [filter]: groupedAccounts[filter] };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading chart of accounts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/accounting')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Accounting Dashboard
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                <Book className="w-10 h-10 text-blue-600" />
                Chart of Accounts
              </h1>
              <p className="text-gray-600">GAAP-compliant account structure for your business</p>
            </div>

            <button
              onClick={() => navigate('/accounting/accounts/new')}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
            >
              <Plus className="w-5 h-5" />
              Add Account
            </button>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-2xl shadow-sm p-4 mb-6">
          <div className="flex gap-2 overflow-x-auto">
            {['ALL', 'ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE', 'COGS'].map((type) => {
              const labels = {
                'ALL': 'All Accounts',
                'ASSET': 'Assets',
                'LIABILITY': 'Liabilities',
                'EQUITY': 'Equity',
                'REVENUE': 'Revenue',
                'EXPENSE': 'Expenses',
                'COGS': 'Cost of Goods Sold'
              };
              return (
                <button
                  key={type}
                  onClick={() => setFilter(type)}
                  className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                    filter === type
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {labels[type]}
                </button>
              );
            })}
          </div>
        </div>

        {/* GAAP Account Number Ranges Info */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-xl p-6 text-white mb-8">
          <h3 className="font-bold text-lg mb-4">GAAP Account Number Ranges</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-sm">
            <div>
              <div className="font-semibold mb-1">Assets</div>
              <div className="opacity-90">1000 - 1999</div>
            </div>
            <div>
              <div className="font-semibold mb-1">Liabilities</div>
              <div className="opacity-90">2000 - 2999</div>
            </div>
            <div>
              <div className="font-semibold mb-1">Equity</div>
              <div className="opacity-90">3000 - 3999</div>
            </div>
            <div>
              <div className="font-semibold mb-1">Revenue</div>
              <div className="opacity-90">4000 - 4999</div>
            </div>
            <div>
              <div className="font-semibold mb-1">Expenses</div>
              <div className="opacity-90">5000 - 9999</div>
            </div>
          </div>
        </div>

        {/* Accounts by Type */}
        {Object.entries(filteredGroups).map(([type, typeAccounts]) => (
          typeAccounts.length > 0 && (
            <div key={type} className="mb-8">
              <div className={`flex items-center gap-3 px-6 py-4 rounded-t-2xl border-2 ${getAccountTypeColor(type)}`}>
                {getAccountTypeIcon(type)}
                <h2 className="text-2xl font-bold">
                  {type.charAt(0) + type.slice(1).toLowerCase()} Accounts
                </h2>
                <span className="ml-auto text-sm font-semibold">
                  {typeAccounts.length} account{typeAccounts.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="bg-white rounded-b-2xl shadow-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="text-left py-4 px-6 font-bold text-gray-700 w-32">Account #</th>
                      <th className="text-left py-4 px-6 font-bold text-gray-700">Account Name</th>
                      <th className="text-left py-4 px-6 font-bold text-gray-700">Description</th>
                      <th className="text-center py-4 px-6 font-bold text-gray-700 w-24">Status</th>
                      <th className="text-right py-4 px-6 font-bold text-gray-700 w-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {typeAccounts.map((account) => (
                      <tr
                        key={account.id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-4 px-6">
                          <span className="font-mono font-bold text-gray-900">
                            {account.account_number}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <span className="font-semibold text-gray-900">
                            {account.account_name}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <span className="text-gray-600 text-sm">
                            {account.description || '—'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span
                            className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                              account.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {account.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/accounting/accounts/${account.id}`)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit account"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAccount(account);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete account"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ))}

        {accounts.length === 0 && !loading && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Accounts Found</h3>
            <p className="text-gray-600 mb-6">
              Get started by creating default GAAP accounts or add your own
            </p>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={handleSeedAccounts}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
              >
                <Book className="w-5 h-5" />
                Create Default Accounts (30 accounts)
              </button>
              <button
                onClick={() => navigate('/accounting/accounts/new')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Create Custom Account
              </button>
            </div>
          </div>
        )}

        {/* GAAP Disclaimer */}
        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
          <h3 className="font-bold text-yellow-900 mb-2">GAAP Compliance Notice</h3>
          <p className="text-sm text-yellow-900">
            This chart of accounts follows Generally Accepted Accounting Principles (GAAP) standards.
            Account numbering follows the standard 5-category system: Assets (1000s), Liabilities (2000s),
            Equity (3000s), Revenue (4000s), and Expenses (5000s+). Always consult with a licensed CPA
            for tax and financial reporting requirements.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChartOfAccountsPage;
